import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { useDebounce } from '@/utils/useDebounce'
import { type Address, parseUnits } from 'viem'
import { useAccount, useConfig, useWriteContract } from 'wagmi'
import { isWhypeVault } from '@/domain/euler/nativeHype'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { invalidateBalancesCache } from '@/domain/cache'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useQueryClient } from '@tanstack/react-query'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { isUserRejection } from '@/lib/errors'
import { useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import {
  type TransactionItems,
  type WithdrawTransactionItem,
  type UnwrapTransactionItem,
  TransactionLabel,
  useSimulateEVCBatchCall,
} from '@eulerxyz/wagmi-evc'

interface UseEWithdrawCollateralDialogParams {
  collateralVault: Address
  collateralAsset: Address
  collateralSymbol: string
  collateralDecimals: number
  collateralLogoURI?: string
  collateralPriceUSD: number
  currentCollateralFormatted: number
  currentDebtUSD: number
  currentLTV: number
  maxLTV: number
  liquidationLTV: number
  accountTotalCollateralUSD?: number
  subAccountId?: number
}

export interface UseEWithdrawCollateralDialogResult {
  collateralToken: Token
  withdrawAmount: string
  setWithdrawAmount: (amount: string) => void
  maxWithdrawable: number
  newLTV: number
  newCollateralUSD: number
  canWithdraw: boolean
  unwrapToNative: boolean
  setUnwrapToNative: (unwrap: boolean) => void
  isWhypeCollateral: boolean
  wouldLiquidate: boolean
  withdrawStatus: ActionHandlerState['status']
  handleExecute: () => void
  isSimulating: boolean
  isSuccess: boolean
  displayError: Error | null
  isTimedOut: boolean
}

export function useEWithdrawCollateralDialog({
  collateralVault,
  collateralAsset,
  collateralSymbol,
  collateralDecimals,
  collateralLogoURI,
  collateralPriceUSD,
  currentCollateralFormatted,
  currentDebtUSD,
  maxLTV,
  accountTotalCollateralUSD,
  subAccountId,
}: UseEWithdrawCollateralDialogParams): UseEWithdrawCollateralDialogResult {
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const { address: userAddress } = useAccount()
  const config = useConfig()
  const queryClient = useQueryClient()
  const invalidateSubaccounts = useSubaccountStore((state) => state.invalidate)

  const { writeContract, data: hash, error: writeError, isPending: isWriting, reset: resetWrite } = useWriteContract()
  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({ hash })

  const [isSuccess, setIsSuccess] = useState(false)

  // Debounce the withdraw amount to prevent RPC spam
  const [keyCounter, setKeyCounter] = useState(0)
  const prevWithdrawAmountRef = useRef(withdrawAmount)

  useEffect(() => {
    if (prevWithdrawAmountRef.current !== withdrawAmount) {
      prevWithdrawAmountRef.current = withdrawAmount
      setKeyCounter((prev) => prev + 1)
    }
  }, [withdrawAmount])

  const { debouncedValue: debouncedWithdrawAmount, isDebouncing } = useDebounce(
    withdrawAmount,
    `withdraw-${keyCounter}`,
    { delay: 300 },
  )

  const position = useMemo(() => {
    if (subAccountId === undefined || subAccountId === null || !userAddress) return undefined
    return getSubAccountAddress(userAddress, subAccountId) as Address
  }, [userAddress, subAccountId])

  const isWhypeCollateral = isWhypeVault(collateralAsset, E_LEND_CHAIN_ID)
  const [unwrapToNative, setUnwrapToNative] = useState(isWhypeCollateral)

  // LTV calculations (unchanged from original)
  const SAFETY_BUFFER = 1.005
  const thisCollateralUSD = currentCollateralFormatted * collateralPriceUSD
  const currentAccountCollateralUSD = accountTotalCollateralUSD ?? thisCollateralUSD
  const minCollateralUSD =
    currentDebtUSD > 0 && maxLTV > 0
      ? ((currentDebtUSD * 100) / maxLTV) * SAFETY_BUFFER
      : currentDebtUSD > 0 && maxLTV === 0
        ? currentAccountCollateralUSD
        : 0
  const maxWithdrawableFromAccountUSD = Math.max(0, currentAccountCollateralUSD - minCollateralUSD)
  const maxWithdrawableUSD = Math.min(maxWithdrawableFromAccountUSD, thisCollateralUSD)
  const maxWithdrawable =
    collateralPriceUSD > 0
      ? maxWithdrawableUSD / collateralPriceUSD
      : currentDebtUSD > 0
        ? 0
        : currentCollateralFormatted

  const collateralToken = useMemo(
    () =>
      new Token({
        symbol: TokenSymbol(collateralSymbol),
        name: collateralSymbol,
        decimals: collateralDecimals,
        address: CheckedAddress(collateralAsset),
        unitPriceUsd: String(collateralPriceUSD),
        logoURI: collateralLogoURI,
      }),
    [collateralSymbol, collateralDecimals, collateralAsset, collateralPriceUSD, collateralLogoURI],
  )

  const withdrawAmountNum = Number(withdrawAmount) || 0
  const withdrawValueUSD = withdrawAmountNum * collateralPriceUSD
  const newAccountCollateralUSD = Math.max(0, currentAccountCollateralUSD - withdrawValueUSD)
  const wouldLiquidate = newAccountCollateralUSD === 0 && currentDebtUSD > 0
  const newLTV =
    newAccountCollateralUSD > 0
      ? (currentDebtUSD / newAccountCollateralUSD) * 100
      : currentDebtUSD > 0
        ? Number.POSITIVE_INFINITY
        : 0
  const newCollateralUSD = Math.max(0, thisCollateralUSD - withdrawValueUSD)

  const LTV_EPSILON = 0.0001
  const canWithdraw =
    withdrawAmountNum > 0 &&
    withdrawAmountNum <= maxWithdrawable &&
    withdrawAmountNum <= currentCollateralFormatted &&
    newLTV <= maxLTV + LTV_EPSILON &&
    (collateralPriceUSD > 0 || currentDebtUSD === 0)

  const isMaxWithdraw = useMemo(() => {
    if (!debouncedWithdrawAmount || Number(debouncedWithdrawAmount) <= 0) return false
    const canWithdrawAll = maxWithdrawable >= currentCollateralFormatted * 0.999
    const isWithdrawingAll = Number(debouncedWithdrawAmount) >= currentCollateralFormatted * 0.999
    return canWithdrawAll && isWithdrawingAll
  }, [debouncedWithdrawAmount, currentCollateralFormatted, maxWithdrawable])

  // Build EVC TransactionItems (replaces Objective creation)
  const transactions = useMemo((): TransactionItems => {
    if (!position || !userAddress || !debouncedWithdrawAmount || Number(debouncedWithdrawAmount) <= 0) return []

    let withdrawBigInt: bigint
    try {
      withdrawBigInt = parseUnits(debouncedWithdrawAmount, collateralDecimals)
    } catch {
      return []
    }
    if (withdrawBigInt === 0n) return []

    const asset = {
      address: collateralAsset,
      name: collateralSymbol,
      symbol: collateralSymbol,
      decimals: collateralDecimals,
    }

    if (unwrapToNative) {
      const unwrapTx: UnwrapTransactionItem = {
        label: TransactionLabel.UNWRAP,
        from: position,
        to: userAddress,
        asset,
        vault: collateralVault,
        value: withdrawBigInt.toString(),
        isMax: isMaxWithdraw,
      }
      return [unwrapTx]
    }

    const withdrawTx: WithdrawTransactionItem = {
      label: TransactionLabel.WITHDRAW,
      from: position,
      to: userAddress,
      asset,
      vault: collateralVault,
      value: withdrawBigInt.toString(),
      isMax: isMaxWithdraw,
    }
    return [withdrawTx]
  }, [
    position,
    userAddress,
    debouncedWithdrawAmount,
    collateralDecimals,
    collateralAsset,
    collateralSymbol,
    collateralVault,
    unwrapToNative,
    isMaxWithdraw,
  ])

  const simulationEnabled = canWithdraw && !isDebouncing && transactions.length > 0

  const {
    data: simulationData,
    isLoading: isSimulating,
    error: simulationError,
  } = useSimulateEVCBatchCall({
    config,
    transactions,
    query: {
      enabled: simulationEnabled,
    },
  })

  const handleExecute = useCallback(() => {
    if (writeError) {
      resetWrite()
    }
    if (!simulationData?.request) {
      console.error('[withdraw-collateral] handleExecute called without simulation data')
      return
    }
    writeContract(simulationData.request as any)
  }, [simulationData, writeContract, writeError, resetWrite])

  const withdrawTimeout = useActionTimeout({
    timeoutMs: 15000,
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess,
    error: writeError || waitError || simulationError,
  })

  const handleExecuteWithTimeout = useCallback(() => {
    withdrawTimeout.handleActionClick(handleExecute)
  }, [withdrawTimeout, handleExecute])

  const withdrawStatus: ActionHandlerState['status'] = useMemo(() => {
    if (isWriting || isWaiting || (hash && !isSuccess && !writeError && !waitError)) return 'loading'
    if (isSuccess) return 'success'
    if (writeError || waitError) return 'error'
    if (!canWithdraw || isDebouncing) return 'disabled'

    const hasBlockingSimulationError = simulationError && !isUserRejection(simulationError)
    if (hasBlockingSimulationError) return 'disabled'

    if (!simulationData) return isSimulating ? 'loading' : 'disabled'
    return 'ready'
  }, [
    isWriting,
    isWaiting,
    isSuccess,
    writeError,
    waitError,
    canWithdraw,
    isDebouncing,
    simulationError,
    simulationData,
    isSimulating,
    hash,
  ])

  useEffect(() => {
    if (isConfirmed && !isSuccess) {
      setIsSuccess(true)
    }
  }, [isConfirmed, isSuccess])

  useEffect(() => {
    if (isConfirmed && userAddress) {
      invalidateBalancesCache({
        queryClient,
        chainId: E_LEND_CHAIN_ID,
        address: userAddress,
        position,
        vaultAddress: collateralVault,
      })
      invalidateSubaccounts()
    }
  }, [isConfirmed, userAddress, queryClient, position, collateralVault, invalidateSubaccounts])

  const displayError = useMemo(() => {
    if (withdrawTimeout.isTimedOut) return withdrawTimeout.displayError
    const errors = [simulationError, writeError, waitError].filter(Boolean)
    return (errors.find((err) => !isUserRejection(err as Error)) as Error) || null
  }, [simulationError, writeError, waitError, withdrawTimeout.isTimedOut, withdrawTimeout.displayError])

  return {
    collateralToken,
    withdrawAmount,
    setWithdrawAmount,
    maxWithdrawable,
    newLTV,
    newCollateralUSD,
    canWithdraw,
    unwrapToNative,
    setUnwrapToNative,
    isWhypeCollateral,
    wouldLiquidate,
    withdrawStatus,
    handleExecute: handleExecuteWithTimeout,
    isSimulating,
    isSuccess,
    displayError,
    isTimedOut: withdrawTimeout.isTimedOut,
  }
}
